// src/context/AuthContext.js
import React, { createContext, useEffect, useState, useContext } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const logout = () => {
    localStorage.removeItem("spotify_access_token");
    localStorage.removeItem("spotify_refresh_token");
    setAccessToken(null);
    setError(null);
    window.location.href = "/";
  };

  const checkTokenExpiration = async (token) => {
    try {
      const response = await fetch("https://api.spotify.com/v1/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        // Token is expired or invalid
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error checking token:", err);
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("access_token") || localStorage.getItem("spotify_access_token");
        const refreshToken = params.get("refresh_token") || localStorage.getItem("spotify_refresh_token");

        if (token) {
          const isValid = await checkTokenExpiration(token);
          if (isValid) {
            setAccessToken(token);
            localStorage.setItem("spotify_access_token", token);
            if (refreshToken) {
              localStorage.setItem("spotify_refresh_token", refreshToken);
            }
          } else {
            logout();
          }
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        setError("Failed to initialize authentication");
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const value = {
    accessToken,
    isLoading,
    error,
    logout,
    isAuthenticated: !!accessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
